// External Imports
import { useContext, useEffect, useState } from "react";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import { UtilityContext } from "../../context/util-context";
import { useSearchParams } from "react-router-dom";
import { requestAdminAPI } from "../../api/posh-api";
import { isoDateToString } from "../../util/format-date";
import { formatCurrency } from "../../util/format-text";
import LinkPair from "../UI/LinkPair";
import uploadFile from "../../util/upload-file";
import LoadingSpinner from "../UI/LoadingSpinner";

// Internal Imports

/** ========================================================================
 * * AddRepossession Component
 * This component renders a form to add a new repossession.
 * ========================================================================== */

export default function AddRepossession() {
  // Load Context
  const { navigate, setConfirm } = useContext(UtilityContext);

  // Get Booking ID from URL
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get("bookingId");

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);

  /* ====================== FETCH BOOKING  ====================== */
  const fetchBooking = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "booking",
        id: bookingId,
        columns: [
          "vehicle { make { name } model year vin cornerImage }",
          "customer { firstName lastName email phone address }",
          "payments: payments_on_booking { amount status }",
        ],
      });
      response.total = 0;
      response.unpaid = 0;
      response.payments.forEach((payment) => {
        if (payment.status !== "succeeded")
          response.unpaid += Number(payment.amount);
        response.total += Number(payment.amount);
      });
      setBooking(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== ADD RESPOSSESSION ====================== */
  const submitHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        bookingId: bookingId,
        address: document.getElementById("address").value,
        reason: document.getElementById("reason").value,
        demandLetter: "",
      };
      // Get Demand Letter
      const demandLetterFile = document.getElementById("demandLetter").files[0];
      if (demandLetterFile)
        payload.demandLetter = await uploadFile(
          `bookings/${bookingId}/demand-letter.${demandLetterFile.name
            .split(".")
            .pop()}`,
          demandLetterFile
        );
      else
        payload.demandLetter = await requestAdminAPI("get-demand-letter", {
          bookingId: bookingId,
        });

      // Submit Repossession
      const response = await requestAdminAPI("add-repossession", payload);

      // Open Repossession Page
      navigate(`/repossessions/info?id=${response}`);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // USE EFFECT
  useEffect(() => {
    if (bookingId) fetchBooking();
  }, [bookingId]);

  if (!bookingId)
    return (
      <div className="container">
        <Heading
          title="Add Repossession"
          text="Fill out the form below to initiate a new vehicle repossession."
        />
        <div className="flex items-center justify-center w-full h-full">
          <p className="text-stone-200">
            Booking ID Not Provided - please go to Booking Information page and
            click "Initiate Repossession".
          </p>
        </div>
      </div>
    );

  return (
    <div className="container grid gap-6">
      <Heading
        title="Initiate Repossession"
        text="Fill out the form below to initiate a new vehicle repossession. The Legal Address should be where the demand letter will be sent. A demand letter will be generated automatically if not uploaded."
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <img src={booking?.vehicle?.cornerImage} alt="Vehicle" />
        <div className="items-start info lg:grid-cols-3 lg:col-span-2">
          <DetailPair
            label="Vehicle"
            loading={loading}
            value={`${booking?.vehicle?.make?.name} ${
              booking?.vehicle?.model
            } ${booking?.vehicle?.year} - ${booking?.vehicle?.vin?.substring(
              13,
              17
            )}`}
          />
          <DetailPair
            label="Start/End Date"
            loading={loading}
            value={`${isoDateToString(booking?.startDate)} - ${isoDateToString(
              booking?.endDate
            )}`}
          />
          <DetailPair
            label="Unpaid/Total Invoices"
            loading={loading}
            value={`${formatCurrency(booking?.unpaid)} / ${formatCurrency(
              booking?.total
            )}`}
          />
          <DetailPair
            label="Customer"
            loading={loading}
            value={`${booking?.customer?.firstName} ${booking?.customer?.lastName}`}
          />
          <DetailPair
            label="Email"
            loading={loading}
            value={`${booking?.customer?.email}`}
          />
          <DetailPair
            label="Phone"
            loading={loading}
            value={`${booking?.customer?.phone}`}
          />
          <div className="lg:col-span-3">
            <DetailPair
              label="Legal Address - where demand letter will be sent"
              loading={loading}
              edit={true}
              inputOptions={{
                id: "address",
                defaultValue: booking?.customer?.address || "",
              }}
            />
          </div>
        </div>
      </div>
      <div className="info">
        <div className="lg:col-span-3">
          <DetailPair
            label="Reason for Repossession"
            edit={true}
            loading={loading}
            inputOptions={{
              id: "reason",
              type: "text",
            }}
          />
        </div>
        <LinkPair
          label="Demand Letter (optional)"
          edit={true}
          loading={loading}
          inputOptions={{
            type: "file",
            id: "demandLetter",
          }}
        />
        {loading ? (
          <LoadingSpinner />
        ) : (
          <button className="btn-primary" onClick={submitHandler}>
            Submit
          </button>
        )}
      </div>
    </div>
  );
}
